import Avatar from 'assets/profileAvatars/Avatar.png';
import { Button, Spinner, StatusBlock } from 'view/components';
import { FavoriteIcon } from 'assets';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import CircularProgress from '@mui/material/CircularProgress';
import { useContextSelector as useCS } from 'use-context-selector';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { useMadeFavoriteById } from 'view/pages/candidates/hooks/useMadeFavoriteById';
import { useNavigate } from 'react-router';
import { useUpdateApplicationStatus } from 'view/pages/candidates/hooks/useUpdateApplicationStatus';
import * as S from './styled';

const CandidateListItem = ({
  candidate,
  successFunction = () => {},
  withStatus = false,
  jobPostId = null,
  queryAfterSubmit = '',
}) => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));

  const isShowMatchScore = useCS(GlobalConfigContext, globalConfigSelectors.isShowMatchScore);
  const { sendFavoriteInfo, isLoading } = useMadeFavoriteById();
  const { isLoading: isUpdateApplicationStatusLoading, updateApplicationStatus } =
    useUpdateApplicationStatus();

  const convertToYearsMonths = (value: string) => {
    const parts = value.split('.');

    const years = parseInt(parts[0], 10);
    const months = parts[1] ? parseInt(parts[1], 10) : 0;

    return `${years} years ${months} month`;
  };

  const getJobLocationDescription = (jobLocation: any) => {
    if (!jobLocation) return '';
    const locations: Array<string> = [];

    if (jobLocation.onSite) locations.push('On Site');
    if (jobLocation.remote) locations.push('Remote');
    if (jobLocation.hybrid) locations.push('Hybrid');

    return locations?.length > 0 ? `(${locations.join(', ')})` : '';
  };

  const getHardSkills = (skills: any) => {
    if (!skills || skills.length === 0) {
      return 'No skills';
    }

    const hardSkills = skills
      .filter((skill) => {
        return skill.group === 'Hard Skills';
      })
      .map((skill) => {
        return skill.name;
      });

    if (hardSkills.length === 0) {
      return 'No skills';
    }

    return hardSkills.join(', ');
  };

  const matchScore = Number(candidate?.score);
  const getMatchScoreColor = (matchScoreParam: number) => {
    if (matchScoreParam > 0 && matchScoreParam <= 25) return '#BF2626';
    if (matchScoreParam > 25 && matchScoreParam <= 50) return '#FF993B';
    if (matchScoreParam > 50 && matchScoreParam <= 75) return '#E5C555';
    if (matchScoreParam > 75) return '#85C553';
    return 'transparent';
  };

  return (
    <S.CandidateListItem>
      <S.CandidateListItemHeading>
        <S.CandidateListItemHeadingInfo>
          <div style={{ width: '72px', height: '72px', position: 'relative' }}>
            <img
              src={candidate?.user?.photo?.link || Avatar}
              alt="avatar"
              style={{ width: '72px', height: '72px' }}
            />
            {isShowMatchScore || Number(candidate?.matchScore) ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={matchScore || Number(candidate?.matchScore)}
                  style={{
                    width: '72px',
                    height: '72px',
                    color: getMatchScoreColor(matchScore || Number(candidate?.matchScore)),
                  }}
                />
              </div>
            ) : null}
          </div>

          <S.CandidateListItemHeadingInfoTexts>
            <S.CandidateListItemHeadingInfoTitle>
              {candidate.jobTitle || 'No Job Title'} <span>{candidate.username}</span>
            </S.CandidateListItemHeadingInfoTitle>
            <S.CandidateListItemHeadingInfoSalary>
              {!candidate.salaryMin && !candidate.salaryMax
                ? 'Salary not specified'
                : `$${candidate.salaryMin.toLocaleString()} - $${candidate.salaryMax.toLocaleString()}`}
            </S.CandidateListItemHeadingInfoSalary>
            {isShowMatchScore || Number(candidate?.matchScore) ? (
              <S.CandidateListItemHeadingInfoSalary style={{ color: '#444444' }}>
                {matchScore || Number(candidate?.matchScore)}% match
              </S.CandidateListItemHeadingInfoSalary>
            ) : null}
          </S.CandidateListItemHeadingInfoTexts>
        </S.CandidateListItemHeadingInfo>
        {!matches && (
          <S.CandidateListItemHeadingButtons>
            <div>
              {withStatus && candidate?.application && (
                <StatusBlock appStatus={candidate?.application?.statusForRecruiter} />
              )}
              <Button
                variant="outlined"
                text=""
                endIcon={
                  isLoading ? (
                    <Spinner variant="outlined" />
                  ) : (
                    <FavoriteIcon style={{ fill: candidate.isFavourited ? '#663497' : '#fff' }} />
                  )
                }
                style={{
                  padding: '10px',
                  width: '40px',
                  minWidth: '40px',
                  height: '40px',
                  minHeight: '40px',
                }}
                onClick={() => {
                  sendFavoriteInfo(candidate.id, successFunction, candidate.isFavourited);
                }}
              />
              <Button
                variant="outlined"
                text="View Profile"
                style={{
                  padding: '10px 20px',
                  width: 'max-content',
                  minWidth: '128px',
                  height: '40px',
                  minHeight: '40px',
                  fontSize: '14px',
                }}
                disabled={isUpdateApplicationStatusLoading}
                endIcon={
                  isUpdateApplicationStatusLoading ? (
                    <Spinner disabled={isUpdateApplicationStatusLoading} variant="contained" />
                  ) : null
                }
                onClick={() => {
                  if (jobPostId && withStatus && candidate?.application) {
                    if (candidate?.application?.statusForRecruiter === 'New') {
                      updateApplicationStatus(
                        {
                          statusForCandidate: 'Applied',
                          statusForRecruiter: 'Pending',
                        },
                        candidate?.application?.id,
                        () => {
                          return navigate(
                            `/candidates/candidate-profile?id=${candidate.id}&jobPostId=${jobPostId}`
                          );
                        }
                      );
                      return;
                    }
                    navigate(
                      `/candidates/candidate-profile?id=${candidate.id}&jobPostId=${jobPostId}`
                    );
                    return;
                  }
                  navigate(
                    `/candidates/candidate-profile?id=${candidate.id}&matchScore=${isShowMatchScore && matchScore ? matchScore : 0}`
                  ); // changed from window open
                }}
              />
            </div>
            <p>
              Updated on{' '}
              {new Date(candidate.updatedAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </S.CandidateListItemHeadingButtons>
        )}
      </S.CandidateListItemHeading>
      <S.Separator />
      <S.CandidateListItemMain>
        <S.CandidateListItemMainTitles>
          <p>Work Experience</p>
          <p>Location</p>
          <p>Technical Skills</p>
        </S.CandidateListItemMainTitles>
        <S.CandidateListItemMainValues>
          <p>
            {candidate.totalExperienceYears === '0.0'
              ? 'Experience not provided'
              : convertToYearsMonths(candidate.totalExperienceYears)}
          </p>
          <p>
            {candidate?.location?.name || 'No location'}{' '}
            <span>{getJobLocationDescription(candidate?.jobLocation)}</span>
          </p>
          <p>{getHardSkills(candidate?.skills)}</p>
        </S.CandidateListItemMainValues>
      </S.CandidateListItemMain>
      {matches && (
        <S.CandidateListItemHeadingButtons style={{ marginTop: '14px', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <Button
              variant="outlined"
              text=""
              endIcon={
                isLoading ? (
                  <Spinner variant="outlined" />
                ) : (
                  <FavoriteIcon style={{ fill: candidate.isFavourited ? '#663497' : '#fff' }} />
                )
              }
              onClick={() => {
                sendFavoriteInfo(candidate.id, successFunction, candidate.isFavourited);
              }}
              style={{
                padding: '10px',
                width: '40px',
                minWidth: '40px',
                height: '40px',
                minHeight: '40px',
              }}
            />
            <Button
              variant="outlined"
              text="View Profile"
              style={{
                padding: '10px 20px',
                /* width: '128px',
                minWidth: '128px', */
                height: '40px',
                minHeight: '40px',
                fontSize: '14px',
              }}
              onClick={() => {
                if (jobPostId && withStatus && candidate?.application) {
                  if (candidate?.application?.statusForRecruiter === 'New') {
                    updateApplicationStatus(
                      {
                        statusForCandidate: 'Applied',
                        statusForRecruiter: 'Pending',
                      },
                      candidate?.application?.id,
                      () => {
                        return navigate(
                          `/candidates/candidate-profile?id=${candidate.id}&jobPostId=${jobPostId}`
                        );
                      }
                    );
                    return;
                  }
                  navigate(
                    `/candidates/candidate-profile?id=${candidate.id}&jobPostId=${jobPostId}`
                  );
                  return;
                }
                navigate(
                  `/candidates/candidate-profile?id=${candidate.id}&matchScore=${isShowMatchScore && matchScore ? matchScore : 0}`
                ); // changed from window open
              }}
            />
          </div>
          <p>
            Updated on{' '}
            {new Date(candidate.updatedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </S.CandidateListItemHeadingButtons>
      )}
    </S.CandidateListItem>
  );
};

export default CandidateListItem;
